import type { JSONContent } from '@tiptap/react';
import { TipoDocumento } from '../services/ModeloDocumentoService';

/* ─────────────────────────────────────────────────────────────
   BIBLIOTECA DE MODELOS DE DOCUMENTO PRONTOS

   Desenho aprovado em 05/10/2026 (Direção A) —
   docs/designsystem/propostas/modelos-de-documento-biblioteca.html.

   O catálogo mora no front, como o dos agentes (modelosDeAgente.ts):
   é texto que vai para o cliente do cliente, então é revisado em PR.
   A modal nunca lê PRONTOS direto, só `listarProntos()` — o dia em
   que a GB Code precisar editar sem deploy, só ela muda.

   TRÊS REGRAS QUE OS PRONTOS SEGUEM, de propósito:

   1. Começam na saudação. O PDF já imprime logo, nome e CNPJ da
      empresa, "Proposta nº 0042", as datas e o título
      (GeradorPdfDocumentoQuestPdf.ComporCabecalho); repetir isso no
      texto sai duplicado.
   2. São por FUNÇÃO, não por ramo: o Brik atende PME de qualquer
      ramo, e o que é do ramo entra pela tabela de itens.
   3. O que só a empresa sabe fica entre colchetes ("[x] dias úteis").
      Por isso o modelo criado a partir de um pronto nasce INATIVO:
      colchete que chega ao cliente é defeito visível.

   O conteúdo é o JSON do Tiptap que a API guarda, com os dois nós
   próprios (`variavel` com `chave`, `tabelaItens`) — ver
   extensoesDocumento.ts. As chaves são as do catálogo da API
   (CatalogoVariaveisDocumento.Fixas).
   ───────────────────────────────────────────────────────────── */

/** O que o modelo precisa que a oportunidade tenha para sair sem trecho em branco. */
export type Requisito = 'contato' | 'contato-endereco' | 'organizacao' | 'itens';

export const NOME_REQUISITO: Record<Requisito, string> = {
    contato: 'Pede contato',
    'contato-endereco': 'Pede contato com endereço',
    organizacao: 'Pede organização',
    itens: 'Pede itens',
};

export interface ModeloPronto {
    /** Chave estável. Nunca renomear. */
    id: string;
    nome: string;
    tipo: TipoDocumento;
    /** A frase do cartão. Duas linhas no máximo. */
    resumo: string;
    pede: Requisito[];
    validadeDias: number | null;
    /** Contrato: o cartão e o passo 2 avisam que é texto-base. */
    textoBase?: boolean;
    conteudo: JSONContent;
}

/* ─── montagem do JSON ──────────────────────────────────────── */

/* Um trecho de texto vira nós: "{contato.nome}" é variável e "**X**"
   é negrito. O resto é texto puro, inclusive os colchetes. */
const inline = (s: string): JSONContent[] => {
    const nos: JSONContent[] = [];
    for (const parte of s.split(/(\{[a-z_.]+\}|\*\*[^*]+\*\*)/g)) {
        if (!parte) continue;
        if (parte.startsWith('{')) nos.push({ type: 'variavel', attrs: { chave: parte.slice(1, -1) } });
        else if (parte.startsWith('**')) nos.push({ type: 'text', text: parte.slice(2, -2), marks: [{ type: 'bold' }] });
        else nos.push({ type: 'text', text: parte });
    }
    return nos;
};

const p = (s: string): JSONContent => ({ type: 'paragraph', content: inline(s) });
const h = (s: string): JSONContent => ({ type: 'heading', attrs: { level: 3 }, content: inline(s) });
const ul = (itens: string[]): JSONContent => ({
    type: 'bulletList',
    content: itens.map(i => ({ type: 'listItem', content: [p(i)] })),
});
const tabela: JSONContent = { type: 'tabelaItens' };
const doc = (...content: JSONContent[]): JSONContent => ({ type: 'doc', content });

/* ─── os seis ───────────────────────────────────────────────── */

const PRONTOS: ModeloPronto[] = [
    {
        id: 'proposta-comercial',
        nome: 'Proposta comercial',
        tipo: TipoDocumento.Proposta,
        resumo: 'Itens, condições e próximos passos, assinada pelo vendedor.',
        pede: ['contato', 'itens'],
        validadeDias: 15,
        conteudo: doc(
            p('Olá, {contato.nome},'),
            p('Obrigado pela conversa. Abaixo estão os itens que combinamos e as condições desta proposta.'),
            h('Itens'),
            tabela,
            h('Condições'),
            ul([
                'Valor total: {oportunidade.valor_total}',
                'Proposta válida até {documento.validade}',
                'Forma de pagamento: [à vista ou parcelado, a combinar]',
            ]),
            h('Próximos passos'),
            p('Para seguir, é só responder por aqui ou escrever para {vendedor.email}.'),
            p('{vendedor.nome}'),
        ),
    },
    {
        id: 'proposta-servico',
        nome: 'Proposta de serviço',
        tipo: TipoDocumento.Proposta,
        resumo: 'Objetivo, escopo, prazo e investimento. O que não está incluído fica escrito.',
        pede: ['contato', 'itens'],
        validadeDias: 15,
        conteudo: doc(
            p('Olá, {contato.nome},'),
            h('Objetivo'),
            p('[o resultado que o cliente quer, em uma frase]'),
            h('Escopo'),
            ul(['[etapa 1]', '[etapa 2]', '[etapa 3]']),
            h('Prazo'),
            p('[x] dias úteis a partir do aceite.'),
            h('Investimento'),
            tabela,
            h('Não está incluído'),
            ul(['[o que fica de fora]']),
            p('{vendedor.nome} · {vendedor.email}'),
        ),
    },
    {
        id: 'orcamento-rapido',
        nome: 'Orçamento rápido',
        tipo: TipoDocumento.Orcamento,
        resumo: 'Três linhas e a tabela, para responder orçamento no balcão.',
        pede: ['contato', 'itens'],
        validadeDias: 7,
        conteudo: doc(
            p('{contato.nome}, segue o orçamento solicitado.'),
            tabela,
            p('Total: {oportunidade.valor_total}. Válido até {documento.validade}.'),
            p('{vendedor.nome} · {vendedor.email}'),
        ),
    },
    {
        id: 'orcamento-condicoes',
        nome: 'Orçamento com condições',
        tipo: TipoDocumento.Orcamento,
        resumo: 'Para empresa: pagamento, prazo de entrega e garantia.',
        pede: ['organizacao', 'itens'],
        validadeDias: 10,
        conteudo: doc(
            p('À {organizacao.nome}, CNPJ {organizacao.cnpj}.'),
            p('A/C {contato.nome}. Segue o orçamento solicitado.'),
            tabela,
            h('Condições de pagamento'),
            p('[à vista com x% de desconto, ou em até x vezes no boleto]'),
            h('Prazo de entrega'),
            p('[x] dias úteis após a confirmação do pedido.'),
            h('Garantia'),
            p('[x] meses contra defeito de fabricação.'),
        ),
    },
    {
        id: 'contrato-servico',
        nome: 'Contrato de prestação de serviços',
        tipo: TipoDocumento.Contrato,
        resumo: 'Partes, objeto, valor, vigência, rescisão e foro. Texto-base.',
        pede: ['organizacao', 'itens'],
        validadeDias: null,
        textoBase: true,
        conteudo: doc(
            p('**CONTRATADA:** {empresa.nome}, CNPJ {empresa.cnpj}.'),
            p('**CONTRATANTE:** {organizacao.nome}, inscrita no CNPJ sob nº {organizacao.cnpj}, com sede em {organizacao.endereco}, neste ato representada por {contato.nome}.'),
            h('1. Objeto'),
            p('A CONTRATADA prestará à CONTRATANTE os serviços listados abaixo.'),
            tabela,
            h('2. Valor'),
            p('{oportunidade.valor_total}, pagos [forma e datas de pagamento].'),
            h('3. Vigência'),
            p('[x] meses a partir de {documento.data_emissao}.'),
            h('4. Rescisão'),
            p('Qualquer das partes pode rescindir este contrato com aviso prévio de 30 dias, por escrito.'),
            h('5. Foro'),
            p('Fica eleito o foro da comarca de [cidade] para dirimir questões deste contrato.'),
            p('[local], {documento.data_emissao}.'),
            p('_______________________________  {empresa.nome}'),
            p('_______________________________  {organizacao.nome}'),
        ),
    },
    {
        id: 'contrato-venda',
        nome: 'Contrato de compra e venda',
        tipo: TipoDocumento.Contrato,
        resumo: 'Comprador pessoa física, entrega e garantia. Texto-base.',
        pede: ['contato-endereco', 'itens'],
        validadeDias: null,
        textoBase: true,
        conteudo: doc(
            p('**VENDEDORA:** {empresa.nome}, CNPJ {empresa.cnpj}.'),
            p('**COMPRADOR(A):** {contato.nome}, residente em {contato.endereco}.'),
            h('1. Objeto'),
            tabela,
            h('2. Preço e pagamento'),
            p('{oportunidade.valor_total}, pagos [forma de pagamento].'),
            h('3. Entrega'),
            p('[prazo e local de entrega].'),
            h('4. Garantia'),
            p('[x] meses, além da garantia legal.'),
            p('[local], {documento.data_emissao}.'),
            p('_______________________________  {empresa.nome}'),
            p('_______________________________  {contato.nome}'),
        ),
    },
];

export const listarProntos = (): ModeloPronto[] => PRONTOS;
